import "./SideBar.scss";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import { CustomSideTab } from "./CustomSideTab/CustomSideTab";
import { canAccessAny, hasPermission } from "../../../utils/permissions";
import { NavLink } from "react-router-dom";
import { useEffect, useMemo, useState, useContext } from "react";
import {
  actions,
  notificationsTriggerActions,
} from "../../../backend/store/reducers/notificationsReducer";
import { useDispatch, useSelector } from "react-redux";
import { ECHO } from "../../../index";
import { useAddNotification } from "../../../hooks/useAddNotification";
import { useTranslation } from "../../../hooks/useTranslation";
import { markAsRead } from "../../../backend/store/actions/creators/notification";
import { Box } from "@mui/material";
import { routes, adminRoutes } from "../../../constants/sideBarRoutes";
import { showsVolunteeringFor } from "../../../utils/showsVolunteering";
import { SelectWorkspace } from "../SelectWorkspace/index";
import { UserContext } from "../../../contexts/UserContext";
import { billingTriggerActions } from "../../../backend/store/reducers/billingReducer";
import ExportDataModal from "../../Onboarding/ExportDataModal";
import ContactUsModal from "../ContactUsModal/ContactUsModal";
import { ImpersonationSidePanel } from "../Impersonation";
import TutorialsModal from "../TutorialsModal/TutorialsModal";
import { setPersonalFrame } from "../../../next/shell/frameMode";
import { isLegacyView, switchLegacyView } from "../../../next/preview/legacyView";
import { useIsSuperAdmin } from "../../../next/bridges/session";
import { LegalDocumentsButton } from "../LegalDocuments/LegalDocumentsButton";

export const SideBar = () => {
  const { p } = useTranslation('sidebar');
  const dispatch = useDispatch();
  const storedUser = useSelector((state: any) => state.authStore.currentUser);
  const currentUser = useMemo(() => storedUser, [storedUser]);
  
  const billingStore = useSelector((state: any) => state.billingStore);
  const hasActiveSubscription = billingStore.hasActiveSubscription;
  const isTrialActive = billingStore.isTrialActive;
  const contactCount = billingStore.contactCount;
  const isPreTrial = billingStore.trialEndsAt === null
      && !billingStore.hasActiveSubscription
      && !billingStore.isSuperAdminWorkspace;
  
  const addNotification = useAddNotification();
  const [valueSelect, setHandleSelect] = useState("");
  const [workspacesUser, setWorkspacesUser] = useState([]);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isTutorialsModalOpen, setIsTutorialsModalOpen] = useState(false);
  const { user } = useContext(UserContext);
  const canTryNewFrame = useIsSuperAdmin();
  const isWorkspaceLocked = localStorage.getItem("workspace_locked") === "true";

  // Load subscription status and usage on mount
  useEffect(() => {
    // Only people with billing rights may read the subscription and usage (the others got a 403 on every page); the lock state they need comes with /me.
    if (currentUser && hasPermission("read_billing", currentUser)) {
      dispatch(billingTriggerActions.getSubscription());
      dispatch(billingTriggerActions.getUsage());
    }
  }, [currentUser, dispatch]);

  useEffect(() => {
    if (!currentUser) return;
    setWorkspacesUser(
      currentUser.workspaces.map((workspace: { name: string; id: number }) => ({
        name: workspace.name,
        id: workspace.id,
      }))
    );

    let myValue: string = "";
    currentUser.workspaces.forEach((element: any) => {
      if (element.id === currentUser.currentWorkspace) {
        myValue = element.name;
        return;
      }
    });
    setHandleSelect(myValue);
  }, [currentUser]);

  // Subscribe to the admin notification channel exactly once per user. ECHO is a single global
  // instance, so `listen()` ADDS a callback rather than replacing it; without removing our
  // handler on cleanup, every re-run of this effect would stack another listener and the same
  // incoming notification would pop up multiple times. Key on the primitive id (not the whole
  // currentUser object, whose reference changes on every getUser() refetch).
  //
  // We remove ONLY our own handler via stopListening(event, handler) — NOT ECHO.leave(), which
  // would tear down the whole `admin.{id}` channel and also kill the import-progress listener
  // that shares it (see ImportProgressContext). A stable handler ref makes add/remove symmetric.
  useEffect(() => {
    if (!currentUser?.id) return;

    const channelName = `admin.${currentUser.id}`;
    const event = "BroadcastNotificationToAdminEvent";

    const handler = (notification: any) => {
      dispatch(notificationsTriggerActions.getNotificationsCountTriggerAC());
      addNotification({
        time: (notification.notification as any).created_at_formatted,
        description: notification.notification.title.startOfTitle,
        additionalDescription: notification.notification.title.endOfTitle,
        userName: notification.notification.title.householdName,
        id: notification.notification.id,
        onClick: (id: string) => {
          dispatch(actions.setSelectedNotification(id));
          dispatch(actions.setVisibilityNotificationModal(true));
          dispatch(
            markAsRead({
              payload: {
                id: notification.notification.id,
              },
            })
          );
        },
      });
    };

    const channel = ECHO.private(channelName);
    // Defensive: drop any handler left behind by a hot-reload / un-cleaned prior mount before
    // (re)binding, so a single broadcast can never fan out to more than one toast.
    channel.stopListening(event, handler).listen(event, handler);

    return () => {
      channel.stopListening(event, handler);
    };
  }, [currentUser?.id]);

  function normalizeScopeType(scopeType?: string): 'routes' | 'households' | undefined {
    if (scopeType === 'routes' || scopeType === 'households') return scopeType;
    return undefined;
  }

  return (
    <>
      <Box className="sidebar" sx={{ display: { xs: "none", md: "flex" }, height: "100%", flexDirection: "column" }}>
        <div className="sidebar__divider" />
        <div className="sidebar__tab-wrapper">
          <ImpersonationSidePanel />
          {/* <Typography className="sidebar__title-general">{p('general')}</Typography> */}
          <List className="sidebar__ul-general">
            {routes
              .filter((item) => item.group === "general")
              .map((element, index) => {
                const isSuperAdmin = currentUser?.role === "super_admin";
                const isBillingPath = element.link === "/billing";
                const isExportItem = element.modal === "export";

                // If not super admin AND no active plan AND trial expired:
                // ONLY show the billing page AND potentially the export button.
                if (!isSuperAdmin && !hasActiveSubscription && !isTrialActive && !isPreTrial) {
                  // If it's the export item, only show if they have data
                  if (isExportItem) {
                    if (!(contactCount > 0)) return null;
                  } else if (!isBillingPath) {
                    return null;
                  }
                } else if (isExportItem) {
                  // If subscription IS active, we don't need the "Emergency Export" in the sidebar
                  // (They can use the regular export on the Contacts page)
                  return null;
                }

                // Skip if user doesn't have permission. Scoped-resource entries (routes/
                // households) use the scope-presence check; scope-less page permissions
                // (e.g. the Map page's dedicated read_map) are matched against general +
                // scoped.permissions via hasPermission.
                if (element.permission) {
                  const scope = normalizeScopeType(element.scopeType);
                  const allowed = scope
                    ? canAccessAny(user, element.permission, scope)
                    : hasPermission(element.permission, user);
                  if (!allowed) {
                    return null;
                  }
                }

                // Task 12: the volunteering entry needs one more condition beyond
                // holding the permission -- see showsVolunteering's own doc comment
                // for why (a volunteer-only user must never be stranded with an
                // empty sidebar, but an account holder who never offered help
                // shouldn't see a new item every anash already has the permission
                // for). Flatten permissions the same way hasPermission does: it
                // checks general OR scoped.permissions, never one alone.
                if (element.requiresVolunteerConnection) {
                  // Whole-branch review fix (MINOR 6), 2026-09-08: the
                  // general + scoped.permissions flattening used to be
                  // hand-copied here and in SideBarMobile -- the exact
                  // duplication showsVolunteering.ts was extracted to avoid.
                  // showsVolunteeringFor() takes the user object and does it once.
                  if (!showsVolunteeringFor(user)) {
                    return null;
                  }
                }

                if (isExportItem) {
                  return (
                    <div
                      key={index}
                      onClick={() => setIsExportModalOpen(true)}
                      style={{ cursor: "pointer" }}
                    >
                      <CustomSideTab
                        icon={element.icon}
                        text={element.route}
                        isActive={false}
                      />
                    </div>
                  );
                }

                if (element.modal === "tutorials") {
                  return (
                    <div
                      key={index}
                      onClick={() => setIsTutorialsModalOpen(true)}
                      style={{ cursor: "pointer" }}
                    >
                      <CustomSideTab
                        icon={element.icon}
                        text={element.route}
                        isActive={false}
                      />
                    </div>
                  );
                }

                return (<NavLink
                  to={element.link}
                  key={index}
                  style={{ textDecoration: "none" }}
                >
                  {({ isActive }) => (
                    <CustomSideTab
                      icon={element.icon}
                      text={element.route}
                      isActive={isActive}
                    />
                  )}
                </NavLink>)
              }
              )
            }
          </List>
          {/* <Typography className="sidebar__title-about">{p('about_us')}</Typography> */}
          <List className="sidebar__ul-about">
            {routes
              .filter((item) => item.group === "about")
              .map((element, index) => {
                // About links (Tutorials, Contact Us) are always visible
                
                // Handle modals
                if (element.modal === "contact_us") {
                  return (
                    <div
                      key={index}
                      onClick={() => setIsContactModalOpen(true)}
                      style={{ cursor: "pointer" }}
                    >
                      <CustomSideTab
                        icon={element.icon}
                        text={element.route}
                        isActive={false}
                      />
                    </div>
                  );
                }

                if (element.modal === "tutorials") {
                  return (
                    <div
                      key={index}
                      onClick={() => setIsTutorialsModalOpen(true)}
                      style={{ cursor: "pointer" }}
                    >
                      <CustomSideTab
                        icon={element.icon}
                        text={element.route}
                        isActive={false}
                      />
                    </div>
                  );
                }
                
                // Handle internal links
                return (
                  <NavLink
                    to={element.link}
                    key={index}
                    style={{ textDecoration: "none" }}
                  >
                    {({ isActive }) => (
                      <CustomSideTab
                        icon={element.icon}
                        text={element.route}
                        isActive={isActive}
                      />
                    )}
                  </NavLink>
                );
              })}
          </List>

          {/* Admin section - Permission-based */}
          {(() => {
            const isSuperAdmin = currentUser?.role === "super_admin";
            const isImpersonating = !!currentUser?.is_impersonating;

            // Never show admin nav during impersonation — impersonated sessions don't have admin access
            if (isImpersonating) {
              return null;
            }

            // Hide admin routes entirely for non-admins with expired trials/no plans
            if (!isSuperAdmin && !hasActiveSubscription && !isTrialActive && !isPreTrial) {
              return null;
            }

            // Filter admin routes based on permissions
            const visibleAdminRoutes = adminRoutes.filter(route =>
              hasPermission(route.permission, currentUser)
            );

            // Only show admin section if user has at least one admin permission
            return visibleAdminRoutes.length > 0 && (
              <>
                <Typography className="sidebar__title-general">
                  {p('admin') || 'Admin'}
                </Typography>
                <List className="sidebar__ul-general">
                  {/* "Show me the old version" is on (staff, saved on the server): the way back to the new design. */}
                  {canTryNewFrame && isLegacyView() && (
                    <CustomSideTab
                      icon={visibleAdminRoutes[0].icon}
                      text="back_to_new_design"
                      isActive={false}
                      onClick={() => { void switchLegacyView(false).catch(() => undefined); }}
                    />
                  )}
                  {canTryNewFrame && !isLegacyView() && (
                    <CustomSideTab
                      icon={visibleAdminRoutes[0].icon}
                      text="try_new_frame"
                      isActive={false}
                      onClick={() => {
                        setPersonalFrame(true);
                        window.location.reload();
                      }}
                    />
                  )}
                  {visibleAdminRoutes.map((element, index) => (
                    <NavLink
                      to={element.link}
                      key={`admin-${index}`}
                      style={{ textDecoration: "none" }}
                    >
                      {({ isActive }) => (
                        <CustomSideTab
                          icon={element.icon}
                          text={element.route}
                          isActive={isActive}
                        />
                      )}
                    </NavLink>
                  ))}
                </List>
              </>
            );
          })()}
          <div className="sidebar__workspace-wrapper">
            {!isWorkspaceLocked && (
              <SelectWorkspace
                label={""}
                list={workspacesUser} // норм
                // list={[
                //   //test
                //   { name: "Workspace name1", id: 1 },
                //   { name: "Workspace name2", id: 2 },
                //   { name: "Workspace name3", id: 3 },
                // ]}
                value={valueSelect}
                placeholder={"Workspace name"}
                hasMultipleWorkspaces={
                  currentUser?.hasMultipleWorkspaces ||
                  workspacesUser.length > 1 ||
                  false
                } // норм
                // hasMultipleWorkspaces={true} //test
                handleSelect={setHandleSelect} // label="Salutation"
                onChangeClose
              //
              // list={salutationOptions}
              // placeholder="Select"
              // value={formik.values.persons[personIndex].salutation}
              // handleSelect={getSalutation}
              />
            )}
            <LegalDocumentsButton variant="sidebar" />
          </div>
        </div>


      </Box>
      <ExportDataModal
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
      />
      <ContactUsModal
        open={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
      <TutorialsModal
        open={isTutorialsModalOpen}
        onClose={() => setIsTutorialsModalOpen(false)}
      />
    </>
  );
};
